/**
 * SpeechReview — 発話区間の LLM レビュー（入力画面の発話タブの頭。Issue #998 でシミュレータの画面から移した）。
 *
 * - 実行ボタン（/duel ReviewSpeechSegments。Issue #157）
 * - デュエル単位の講評（カット概要・残した見どころ・演者へのフィードバック）の折りたたみ表示。
 *   duel_games.speech_review_* はGetDuelGameInfoのレスポンスにそのまま含まれる
 *
 * レビューはLLMの応答が10分を超えうるため、サーバー側は export_requests に
 * kind='speech_review' で積んで即返す（synchro_server/speech_review.py）。
 * ここでは投入後に GetExportRequests をポーリングして状態を出し、完了したら
 * onReviewed() で講評・発話区間の再取得を親に依頼する。
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../../api/api';
import { btn } from './RespondTab';

const SECTIONS = [
  { key: 'speech_review_cut_overview', label: 'カット概要' },
  { key: 'speech_review_kept_highlights', label: '残した見どころ' },
  { key: 'speech_review_performer_feedback', label: '演者へのフィードバック' },
];

const POLL_INTERVAL_MS = 10000;
const ACTIVE_STATUSES = ['pending', 'processing'];

const STATUS_TEXT = {
  pending: '待機中...',
  processing: 'レビュー中...',
};

export default function SpeechReview({ duelProject, onReviewed }) {
  const [open, setOpen] = useState(false);
  const [request, setRequest] = useState(null);   // 進行中/直近の export_request
  const [error, setError] = useState('');
  const [starting, setStarting] = useState(false);

  const gameId = duelProject?.id;
  // 完了を検知したときだけ親へ再取得を依頼する。onReviewed の同一性に依存して
  // ポーリングを張り直さないよう ref 経由で参照する
  const onReviewedRef = useRef(onReviewed);
  onReviewedRef.current = onReviewed;

  const fetchLatest = useCallback(async () => {
    if (!gameId) return null;
    // _request() が {"data": [...]} を剥がすので配列がそのまま返る。
    // GetExportRequests は created_at DESC なので先頭が最新（再実行の履歴が並ぶ）
    const res = await api.getExportRequests({ game_id: gameId, kind: 'speech_review' });
    return Array.isArray(res) ? (res[0] ?? null) : null;
  }, [gameId]);

  // マウント時に進行中のレビューを拾う（別のタブ・CLIから走らせた場合や再読み込み後）
  useEffect(() => {
    let cancelled = false;
    fetchLatest()
      .then(latest => { if (!cancelled && latest && ACTIVE_STATUSES.includes(latest.status)) setRequest(latest); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [fetchLatest]);

  // 進行中のあいだだけポーリングする
  useEffect(() => {
    if (!request || !ACTIVE_STATUSES.includes(request.status)) return undefined;

    let cancelled = false;
    const timer = setInterval(async () => {
      let latest;
      try {
        latest = await fetchLatest();
      } catch {
        return;  // 一過性の失敗は次の周期で拾い直す
      }
      // awaitの解決がアンマウント後になることがある
      if (cancelled || !latest) return;
      setRequest(latest);
      if (ACTIVE_STATUSES.includes(latest.status)) return;

      if (latest.status === 'completed') {
        onReviewedRef.current?.();
      } else if (latest.status === 'failed') {
        setError(latest.error_message || 'レビューに失敗しました。');
      }
    }, POLL_INTERVAL_MS);

    return () => { cancelled = true; clearInterval(timer); };
  }, [request, fetchLatest]);

  const startReview = async () => {
    if (!gameId || starting) return;
    if (!window.confirm(
      '発話区間をLLM(Claude API)にまとめて渡し、enable/disableを直接反映します'
      + '（テキスト修正は提案として保存のみ）。\nClaude APIの利用料金が発生します。実行しますか？'
    )) return;

    setStarting(true);
    setError('');
    try {
      const res = await api.reviewSpeechSegments(gameId);
      if (res?.error) {
        setError(res.error);
      } else {
        setRequest({ id: res.id, status: res.status ?? 'pending' });
      }
    } catch (e) {
      setError(e.message || 'レビューの開始に失敗しました。');
    } finally {
      setStarting(false);
    }
  };

  const hasSummary = SECTIONS.some(s => duelProject?.[s.key]);
  const running = !!request && ACTIVE_STATUSES.includes(request.status);
  const statusText = running ? (STATUS_TEXT[request.status] ?? request.status) : '';

  return (
    <div data-testid="speech-review" style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: '6px 0', borderBottom: '1px solid #1c2440' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        {hasSummary ? (
          <button type="button" data-testid="speech-review-toggle" aria-expanded={open} onClick={() => setOpen(v => !v)}
            style={{ background: 'none', border: 'none', color: '#e9c46a', fontSize: 13, padding: 0, cursor: 'pointer' }}>
            {open ? '▼' : '▶'} LLM レビューの講評
          </button>
        ) : (
          <span style={{ color: '#8896b3', fontSize: 13 }}>LLM レビューはまだです</span>
        )}
        <button type="button" data-testid="speech-review-run" onClick={startReview} disabled={!gameId || starting || running}
          style={{ ...btn('plain'), minHeight: 32, padding: '4px 12px', fontSize: 13, marginLeft: 'auto' }}>
          {running ? statusText : (starting ? '送信中…' : 'LLM レビューを実行')}
        </button>
      </div>
      {/* progress はサーバーが完了時にだけ埋める（反映件数のまとめ）。進行中に出るものではないので running では出さない */}
      {request?.status === 'completed' && request.progress ? <div style={{ color: '#8896b3', fontSize: 12 }}>{request.progress}</div> : null}
      {error ? <div role="alert" style={{ color: '#f99', fontSize: 12 }}>{error}</div> : null}
      {hasSummary && open ? (
        <div data-testid="speech-review-summary" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {SECTIONS.map(s => duelProject[s.key] && (
            <div key={s.key}>
              <div style={{ fontSize: 11, color: '#e9c46a', marginBottom: 2 }}>{s.label}</div>
              <div style={{ fontSize: 13, color: '#cfd6e6', whiteSpace: 'pre-wrap', lineHeight: 1.5, userSelect: 'text', WebkitUserSelect: 'text' }}>{duelProject[s.key]}</div>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}
